import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../../components/Seo';
import FaqSection from '../../components/Faq';
import { useTheme } from '../../theme';
import { articleSchema, faqPageSchema, breadcrumbSchema, Faq } from '../../lib/schema';

const PATH = '/base64-for-email-attachments';

const FAQS: Faq[] = [
  { q: 'Why are email attachments Base64 encoded?', a: 'Email (SMTP) was originally designed to carry only 7-bit ASCII text. Base64 turns any binary file — images, PDFs, zip archives — into plain ASCII text so it can travel safely through the email system.' },
  { q: 'What is MIME in relation to email attachments?', a: 'MIME (Multipurpose Internet Mail Extensions, RFC 2045) is the standard that defines how email messages carry multiple parts (text, HTML, attachments), including the rule that binary parts are Base64 encoded and wrapped at 76 characters per line.' },
  { q: 'Why is the line wrapped at 76 characters?', a: 'Historic SMTP relays enforced maximum line lengths and were not fully 8-bit clean; wrapping at 76 characters (with CRLF) keeps every line safely within limits that all mail servers were guaranteed to support.' },
  { q: 'Does this affect attachment file size?', a: 'Yes — Base64 adds about 33% overhead on top of the line-break characters, so a 3 MB attachment becomes roughly 4 MB or slightly more of email payload data.' },
];

export default function Base64ForEmailAttachmentsPage() {
  const { themeClasses } = useTheme();
  const heading = `text-2xl font-bold ${themeClasses.textPrimary} mt-10 mb-4`;
  const para = `${themeClasses.textSecondary} leading-relaxed mb-4`;

  return (
    <>
      <Seo
        title="Base64 for Email Attachments: How MIME Encoding Works | EasyBase64"
        description="Learn why email attachments are Base64 encoded, how MIME (RFC 2045) wraps them at 76 characters per line, and how much size overhead it adds."
        path={PATH}
        jsonLd={[
          articleSchema({
            headline: 'Base64 for Email Attachments: How MIME Encoding Works',
            description: 'How and why email attachments are Base64 encoded using the MIME standard.',
            path: PATH,
            datePublished: '2026-07-07',
            dateModified: '2026-07-07',
          }),
          faqPageSchema(FAQS),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Base64 for Email Attachments', path: PATH }]),
        ]}
      />

      <div className="max-w-3xl mx-auto">
        <article className={`${themeClasses.infoCard} rounded-2xl p-6 sm:p-8 md:p-12 border mb-12`}>
          <h1 className={`text-4xl font-bold tracking-tight ${themeClasses.textPrimary} mb-6`}>
            Base64 for Email Attachments: How MIME Encoding Works
          </h1>

          <p className={para}>
            Every image, PDF or zip file you've ever emailed traveled through the internet as plain
            text, thanks to Base64. Here's why that's necessary and how the format actually works.
          </p>

          <h2 className={heading}>Email was built for text, not binary</h2>
          <p className={para}>
            SMTP, the protocol email runs on, was designed in an era when messages were assumed to
            be 7-bit ASCII text. Many mail relays along the way were never guaranteed to pass
            arbitrary binary bytes through unchanged — some would strip the high bit, interpret
            certain byte sequences as control codes, or mangle line endings. Attaching a raw binary
            file directly risked silent corruption.
          </p>

          <h2 className={heading}>MIME's solution: Base64 + line wrapping</h2>
          <p className={para}>
            MIME (RFC 2045) solves this by Base64 encoding binary attachments into safe ASCII text,
            then wrapping the result at 76 characters per line with CRLF line breaks — a stricter
            variant of plain Base64. This is different from the unwrapped Base64 used in{' '}
            <Link to="/base64-data-uri-guide" className="underline">data URIs</Link> or{' '}
            <Link to="/jwt-base64url-encoding" className="underline">JWTs</Link>, which don't need to
            survive line-length-limited legacy transports. Try it yourself with the{' '}
            <Link to="/base64-mime-encode" className="underline">MIME Base64 encoder</Link>.
          </p>

          <h2 className={heading}>What the raw email actually contains</h2>
          <p className={para}>
            If you view an email's raw source, an attachment appears as a MIME part with headers
            like <code>Content-Type: image/png</code> and{' '}
            <code>Content-Transfer-Encoding: base64</code>, followed by the Base64-encoded, line-wrapped
            body. Your mail client decodes this automatically and re-renders the original file —
            it's Base64 all the way down.
          </p>

          <h2 className={heading}>The size cost</h2>
          <p className={para}>
            Base64 adds roughly a{' '}
            <Link to="/base64-file-size-overhead" className="underline">33% size overhead</Link>{' '}
            on top of line-break characters, so a 3&nbsp;MB attachment becomes a bit over 4&nbsp;MB
            of actual message payload — one reason many email providers cap attachment size well
            below their stated mailbox storage limits.
          </p>

          <h2 className={heading}>Note: this is not encryption</h2>
          <p className={para}>
            Like all Base64 use, MIME encoding provides zero confidentiality — see{' '}
            <Link to="/is-base64-encryption" className="underline">is Base64 encryption?</Link>.
            Anyone who intercepts the raw email can decode the attachment trivially; real
            confidentiality for email comes from transport encryption (TLS between mail servers) or
            end-to-end encryption standards like PGP/S-MIME.
          </p>
        </article>

        <FaqSection title="Base64 for Email Attachments — FAQ" faqs={FAQS} />
      </div>
    </>
  );
}
